import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Header } from "@tanstack/react-table";
import cx from "classnames";
import type React from "react";
import { type CSSProperties, memo, useCallback, useMemo, useRef } from "react";
import { t } from "ttag";

import S from "./SortableHeader.module.css";

// if header is dragged fewer than this number of pixels we consider it a click instead of a drag
const HEADER_DRAG_THRESHOLD = 8;
const COLUMN_RESIZE_STEP = 10;

type DragPosition = { x: number; y: number };

export interface SortableHeaderProps<TData, TValue> {
  children: React.ReactNode;
  className?: string;
  isColumnReorderingDisabled?: boolean;
  style?: React.CSSProperties;
  header: Header<TData, TValue>;
  onClick?: (e: React.MouseEvent<HTMLDivElement>, columnId: string) => void;
}

export const SortableHeader = memo(function SortableHeader<TData, TValue>({
  header,
  className,
  children,
  isColumnReorderingDisabled,
  style: styleProp,
  onClick,
}: SortableHeaderProps<TData, TValue>) {
  const canResize = header.column.getCanResize();
  const headerClickTargetSelector =
    header.column.columnDef.meta?.headerClickTargetSelector;

  const id = header.column.id;
  const minColumnSize = header.column.columnDef.minSize ?? 0;
  const maxColumnSize =
    header.column.columnDef.maxSize ?? Number.MAX_SAFE_INTEGER;
  const currentColumnSize = header.column.getSize();
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id,
      disabled: isColumnReorderingDisabled,
    });

  const dragStartPosition = useRef<DragPosition | null>(null);

  const rootStyle = useMemo<CSSProperties>(() => {
    if (isColumnReorderingDisabled) {
      return styleProp ?? {};
    }
    return {
      position: "relative",
      transform: isDragging ? CSS.Translate.toString(transform) : undefined,
      transition: "width transform 0.2s ease-in-out",
      whiteSpace: "nowrap",
      zIndex: isDragging ? 2 : 0,
      cursor: isDragging ? "grabbing" : "pointer",
      outline: "none",
      ...styleProp,
    };
  }, [isDragging, transform, isColumnReorderingDisabled, styleProp]);

  const nodeAttributes = useMemo(() => {
    if (isColumnReorderingDisabled) {
      return {};
    }

    return {
      ...listeners,
      ...attributes,
    };
  }, [attributes, isColumnReorderingDisabled, listeners]);

  const handleDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    dragStartPosition.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleDragEnd = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (dragStartPosition.current) {
        const dx = Math.abs(e.clientX - dragStartPosition.current.x);
        const dy = Math.abs(e.clientY - dragStartPosition.current.y);

        const isClicked = dx + dy < HEADER_DRAG_THRESHOLD;
        const isClickTarget = headerClickTargetSelector
          ? !!(e.target as HTMLElement).closest(headerClickTargetSelector)
          : true;
        if (isClicked && onClick && isClickTarget) {
          onClick(e, id);
        }

        dragStartPosition.current = null;
      }
    },
    [id, onClick, headerClickTargetSelector],
  );

  const resizeHandler = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      header.getResizeHandler()(e);
      e.stopPropagation();
    },
    [header],
  );

  const resizeColumnWithKeyboard = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let sizeDelta = 0;
      if (e.key === "ArrowLeft") {
        sizeDelta = -COLUMN_RESIZE_STEP;
      } else if (e.key === "ArrowRight") {
        sizeDelta = COLUMN_RESIZE_STEP;
      }

      if (sizeDelta === 0) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const newColumnSize = Math.min(
        maxColumnSize,
        Math.max(minColumnSize, currentColumnSize + sizeDelta),
      );

      header.getContext().table.setColumnSizing((currentSizing) => ({
        ...currentSizing,
        [id]: newColumnSize,
      }));
    },
    [currentColumnSize, header, id, maxColumnSize, minColumnSize],
  );

  return (
    <div
      ref={setNodeRef}
      className={cx(S.root, className)}
      style={rootStyle}
      onMouseDown={handleDragStart}
      onMouseUp={handleDragEnd}
    >
      <div className={S.headerContent} {...nodeAttributes}>
        {children}
      </div>
      {canResize ? (
        <div
          data-testid={`resize-handle-${id}`}
          className={S.resizeHandle}
          role="separator"
          aria-label={t`Resize column`}
          aria-orientation="vertical"
          aria-valuemin={minColumnSize}
          aria-valuemax={maxColumnSize}
          aria-valuenow={currentColumnSize}
          aria-valuetext={t`${currentColumnSize} pixels`}
          tabIndex={0}
          onMouseDown={resizeHandler}
          onTouchStart={resizeHandler}
          onKeyDown={resizeColumnWithKeyboard}
          onMouseOver={(e) => e.stopPropagation()}
        />
      ) : null}
    </div>
  );
}) as <TData, TValue>(
  props: SortableHeaderProps<TData, TValue>,
) => React.ReactElement;
